import { useLocalization } from '@components/Localization'
import LocalizedLink from '@components/Localization/LocalizedLink'
import { getProductInformationProps } from '@components/_product-detail/ProductInformation/utils'
import { externalProducts } from '@config/externalProducts'
import {
  Box,
  breakpoints,
  CleanButton,
  Flex,
  heading4,
  Icon,
  Image,
  Link,
  Text,
  theme
} from '@gassan-ui'
import { Article_article } from '@graphql/__generated__/Article'
import { ArticlesByID_articlesById } from '@graphql/__generated__/ArticlesByID'
import { imgix } from '@lib/imigx'
import { motion } from 'framer-motion'
import React, { FC } from 'react'
import styled from 'styled-components'
import * as S from './elements'
import { Description, Parent } from './elements'
import Price from './Price'

type Props = {
  item: ArticlesByID_articlesById
  onRemove: () => void
  onAddToCart: (id: string) => void
}

const Title = styled.a`
  ${heading4};
  margin-bottom: 0;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      text-decoration: underline;
    }
  }

  @media screen and (min-width: ${breakpoints.medium}) {
    margin-bottom: 0.5rem;
  }

  @media screen and (min-width: ${breakpoints.xlarge}) {
    margin-bottom: 1rem;
  }
`

const Row: FC<Props> = ({ item, onRemove, onAddToCart }) => {
  const { lang } = useLocalization()
  const status = getProductInformationProps(item as Article_article, lang)
  let variant: 'default' | 'no-cart' | 'external' = 'default'
  if (!status.buttons.includes('order')) {
    variant = 'no-cart'
  }
  if (typeof externalProducts[item.id] !== 'undefined') {
    variant = 'external'
  }
  return (
    <motion.div animate exit={{ opacity: 0 }} transition={{ duration: 0.2, ease: 'easeOut' }}>
      <Box
        borderBottom={`1px solid ${theme.colors.shades[200]}`}
        pb={{ _: '1rem', medium: '1.75rem', large: '2rem' }}
        mb={{ _: '1rem', medium: '1.75rem', large: '2rem' }}
      >
        <Parent alignItems="flex-start">
          <LocalizedLink href="/p/[slug]" as={`/p/${item.urlSlug}`}>
            <Box bg="shades.100" position="relative" paddingTop="100%" as="a">
              {item.images[0] && (
                <Image
                  height="100%"
                  width="100%"
                  position="absolute"
                  top="0"
                  sources={[imgix(item.images[0], { width: 200 })]}
                  alt={item.brand}
                  backgroundColor="shades.100"
                />
              )}
            </Box>
          </LocalizedLink>
          <Box display={{ _: 'flex' }} flexDirection="column" height="100%">
            <Flex justifyContent="space-between" alignItems="center">
              <LocalizedLink href="/p/[slug]" as={`/p/${item.urlSlug}`}>
                <Title>{item.brand}</Title>
              </LocalizedLink>
              <Box display={['contents', 'none']} color="tabaccoBrown">
                <CleanButton onClick={onRemove}>
                  <Icon icon="trash-can" />
                </CleanButton>
              </Box>
            </Flex>
            <Box display={['contents', 'flex']} alignItems="center">
              <Description mb={{ _: '0.25rem', small: '0.5rem', large: '1rem' }}>
                {item.description}
              </Description>
              <Box display={['contents', 'none']}>
                <Price
                  price={item.price}
                  salePrice={item.salePrice && item.salePrice}
                  discount={item.salePrice && Math.round((1 - item.salePrice / item.price) * 100)}
                />
              </Box>
            </Box>
            <Flex alignItems="center" mt={{ small: 'auto', large: 'initial' }}>
              <Box display={['none', 'block']}>
                <Link as="button" onClick={onRemove} variant="brown">
                  {lang === 'nl' ? 'Verwijder' : 'Remove'}
                </Link>
              </Box>
              <Box
                height="0.25rem"
                width="0.25rem"
                borderRadius="100%"
                bg="shades.300"
                mx="1rem"
                display={['none', 'block']}
              />
              {variant === 'default' && (
                <Link as="button" onClick={() => onAddToCart(item.id)} variant="brown">
                  {lang === 'nl' ? 'In winkelwagen' : 'Add to cart'}
                </Link>
              )}
              {variant === 'no-cart' && (
                <LocalizedLink href="/p/[slug]?appointment=true" as={`/p/${item.urlSlug}`}>
                  <Link as="link" variant="brown">
                    {lang === 'nl' ? 'Maak een afspraak' : 'Make an appointment'}
                  </Link>
                </LocalizedLink>
              )}
              {variant === 'external' && (
                <Link as="link" variant="brown" href={externalProducts[item.id]} target="_blank">
                  {lang === 'nl' ? 'Bekijken' : 'View'}
                </Link>
              )}
            </Flex>
          </Box>
          <Box display={['none', 'contents']}>
            <Price
              price={item.price}
              salePrice={item.salePrice && item.salePrice}
              discount={item.salePrice && Math.round((1 - item.salePrice / item.price) * 100)}
            />
          </Box>
        </Parent>
        {variant === 'no-cart' && (
          <S.NotAvailable p={['0.5rem 0.75rem', '0.75rem 1rem']} mt={['1rem']}>
            <Text variant="small" color="tabaccoBrown" mb="0">
              {lang === 'nl'
                ? 'Product niet beschikbaar, zie productpagina voor details'
                : 'Product not available, see product page for details'}
            </Text>
          </S.NotAvailable>
        )}
      </Box>
    </motion.div>
  )
}

export default Row
